import React, { useState, useRef, useEffect, useMemo } from "react";
import {
  Box,
  TextField,
  IconButton,
  Typography,
  Alert,
  Divider,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  formatCurrency,
  moveToIndex,
  parseLocalizedNumber,
  round,
  rowType,
} from "../helpers";

const DynamicBudgetInput = ({
  unit_id,
  budget,
  setBudget,
  setEbsTotal,
  internalStaffCost,
}) => {
  const [nameError, setNameError] = useState("");
  const phantomNameRef = useRef(null);
  const rowValueRef = useRef(null);
  const rowNameRef = useRef(null);
  const suppressSelectRef = useRef(false);
  const createdRef = useRef(false);

  const [rows, setRows] = useState(null);
  const [staffCost, setStaffCost] = useState({
    name: "Internal Staff Cost",
    value: 0,
    value_display: "0,00",
    row_type: rowType.staffCost,
  });

  const handleRowChange = (index, field, value) => {
    const updatedRows = [...rows];
    updatedRows[index][field] = value;
    if (
      field === "value" &&
      unit_id === 4 &&
      (updatedRows[index].row_type === rowType.approvedBudget ||
        updatedRows[index].row_type === rowType.selfContribution)
    ) {
      updatedRows[2].value_display = formatCurrency(
        (updatedRows[0].value * (100 - updatedRows[1].value)) / 100,
      );
      updatedRows[2].value =
        (updatedRows[0].value * (100 - updatedRows[1].value)) / 100;
      console.log(updatedRows[2]);
    } else if (index === 2 && field === "value") {
      updatedRows[0].value_display = formatCurrency(updatedRows[2].value);
      updatedRows[0].value = updatedRows[2].value;
      updatedRows[1].value_display = formatCurrency(0);
      updatedRows[1].value = 0;
    }
    setRows(updatedRows);
  };

  const handleRemoveRow = (index) => {
    const updatedRows = [...rows];
    updatedRows.splice(index, 1);
    setRows(updatedRows);
  };

  const commitPhantomRow = (name, value, queued) => {
    let parsed = value === null ? 0 : parseLocalizedNumber(value);
    if (isNaN(parsed)) parsed = 0;
    const updatedRows = [...rows];
    if (queued) {
      const lastRow = updatedRows[updatedRows.length - 1];
      lastRow.name = lastRow.name + name;
      lastRow.value = 10 * lastRow.value + parsed;
      lastRow.value_display = 10 * lastRow.value_display + parsed;
    } else {
      updatedRows.push({ name: name, value: parsed, value_display: parsed });
    }
    setRows(updatedRows);
  };

  const handleBlurRound = (index) => {
    const updatedRows = [...rows];
    let num = parseLocalizedNumber(updatedRows[index].value);
    if (isNaN(num)) num = 0;
    updatedRows[index].value_display = formatCurrency(num);
    updatedRows[index].value = round(num, 2);
    if (rows.find((row, i) => i !== index && row.name === updatedRows[index])) {
      setNameError(`Cost with name '${updatedRows[index]}' already exists!`);
      return;
    }
    setRows(updatedRows);
    setBudget([
      ...updatedRows,
      staffCost,
      {
        name: "EBS Revenue",
        value: ebs,
        value_display: formatCurrency(ebs),
        row_type: rowType.revenue,
      },
    ]);
  };

  const ebs = useMemo(
    () =>
      rows
        ?.filter(
          (r) =>
            r.row_type !== rowType.approvedBudget &&
            r.row_type !== rowType.selfContribution,
        )
        ?.reduce((acc, row, i) => {
          const n = parseFloat(row.value);
          const v = isNaN(n) ? 0 : n;
          return i === 0 ? v : acc - v; // first element sets the initial value
        }, 0) - staffCost.value ?? 0,
    [rows, staffCost],
  );
  const getPercentage = (value) => {
    const parsed = parseFloat(value);
    const total = rows?.find((r) => r.row_type === rowType.total)?.value;
    if (isNaN(parsed) || isNaN(total) || total === 0) return "0,00%";
    return `${formatCurrency((parsed / total) * 100)}%`;
  };

  useEffect(() => {
    if ((budget?.length ?? 0) === 0) {
      setRows([
        {
          name: "Budget Approved By Funder",
          value_display: "0,00",
          value: 0,
          row_type: rowType.approvedBudget,
        },
        {
          name: "EBS Own Contribution",
          value_display: "0,00",
          value: 0,
          row_type: rowType.selfContribution,
        },
        {
          name: "Total",
          value_display: "0,00",
          value: 0,
          row_type: rowType.total,
        },
      ]);
    } else {
      const arr = [
        ...budget.filter(
          (row) =>
            row.row_type === rowType.total ||
            row.row_type === rowType.approvedBudget ||
            row.row_type === rowType.selfContribution ||
            row.row_type == null,
        ),
      ];
      moveToIndex(arr, 0, (x) => x.row_type === rowType.approvedBudget);
      moveToIndex(arr, 1, (x) => x.row_type === rowType.selfContribution);
      moveToIndex(arr, 2, (x) => x.row_type === rowType.total);
      setRows(arr);
    }
  }, [budget]);

  useEffect(() => {
    if (!rows) return;
    const updatedRows = [...rows];
    for (const row of updatedRows) {
      if (row.value_display != null) return;
      row.value_display = formatCurrency(row.value);
    }
  }, [rows, setRows]);

  useEffect(() => {
    setEbsTotal(ebs);
  }, [ebs, setEbsTotal]);

  useEffect(() => {
    setStaffCost((prev) => {
      const updStaffCost = { ...prev };
      updStaffCost.value = internalStaffCost;
      updStaffCost.value_display = formatCurrency(internalStaffCost);

      return updStaffCost;
    });
    setBudget((prev) => {
      if (!prev) return [];
      const updBudget = [...prev];
      const staff = updBudget.find((row) => row.row_type === rowType.staffCost);
      staff.value = internalStaffCost;
      staff.value_display = formatCurrency(internalStaffCost);
      const revenue = updBudget.find((row) => row.row_type === rowType.revenue);
      revenue.value = ebs;
      revenue.value_display = formatCurrency(ebs);
      return updBudget;
    });
  }, [internalStaffCost, setBudget, ebs]);

  const renderRow = (
    row,
    index,
    nameDisabled,
    valueDisabled,
    deletable,
    showPercentage,
  ) => (
    <Box
      key={index}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 2,
        marginBottom: 2,
        width: "100%",
      }}
    >
      <TextField
        placeholder="Add Cost"
        value={row.name || ""}
        disabled={nameDisabled}
        onFocus={(e) => {
          if (suppressSelectRef.current) {
            suppressSelectRef.current = false;
            createdRef.current = false;
            return;
          }
          e.target.select();
        }}
        onChange={(e) => handleRowChange(index, "name", e.target.value)}
        onBlur={() => {
          handleBlurRound(index);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.target.blur();
            setTimeout(() => {
              phantomNameRef.current?.focus();
            }, 0);
          }
        }}
        size="small"
        fullWidth
        inputProps={{
          maxLength: 255,
        }}
        InputProps={{
          sx: {
            // actual <input> element (disabled)
            "&.Mui-disabled .MuiInputBase-input": {
              cursor: "text", // I-beam over the text area
            },
          },
        }}
        sx={(t) => ({
          "& .MuiOutlinedInput-notchedOutline": {
            border: nameDisabled ? "none" : "default",
          },
          width: "50%",
          "& .MuiInputBase-input.Mui-disabled": {
            WebkitTextFillColor: t.palette.text.primary, // Safari/Chrome
            color: t.palette.text.primary, // fallback
            opacity: 1,
          },
        })}
        inputRef={rowNameRef}
      />
      <TextField
        placeholder="Value"
        type="decimal"
        value={row.value_display || ""}
        disabled={valueDisabled}
        onFocus={(e) => {
          if (suppressSelectRef.current) {
            suppressSelectRef.current = false;
            createdRef.current = false;
            return;
          }
          e.target.select();
        }}
        onChange={(e) => {
          handleRowChange(index, "value_display", e.target.value);
          handleRowChange(index, "value", parseLocalizedNumber(e.target.value));
        }}
        onBlur={() => {
          handleBlurRound(index);
        }}
        onKeyDown={(e) => {
          if (
            e.key === "Enter" ||
            (rows.length - 1 === index && e.key === "Tab")
          ) {
            e.target.blur();
            setTimeout(() => {
              phantomNameRef.current?.focus();
            }, 0);
          }
        }}
        size="small"
        fullWidth
        inputProps={{
          min: 0,
          step: "any",
          maxLength: 16,
          max: row.row_type === rowType.selfContribution ? 100 : undefined,
        }}
        InputProps={{
          startAdornment: (
            <Box sx={{ color: "black" }}>
              {row.row_type === rowType.selfContribution ? "%" : "€"}&nbsp;
            </Box>
          ),
          endAdornment: showPercentage && (
            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              <Typography variant="caption" color="text.secondary">
                {getPercentage(row.value)}
              </Typography>
            </Box>
          ),
          sx: {
            // actual <input> element (disabled)
            "&.Mui-disabled .MuiInputBase-input": {
              cursor: "text", // I-beam over the text area
            },
          },
        }}
        inputRef={rowValueRef}
        sx={(t) => ({
          width: !deletable ? "45%" : "50%",
          "& .MuiOutlinedInput-notchedOutline": {
            border: valueDisabled ? "none" : "default",
          },
          "& .MuiInputBase-input.Mui-disabled": {
            WebkitTextFillColor: t.palette.text.primary, // Safari/Chrome
            color: t.palette.text.primary, // fallback
            opacity: 1,
          },
        })}
      />
      {deletable && (
        <IconButton
          onClick={() => handleRemoveRow(index)}
          sx={{
            width: "3%",
            color: "black",
            "&:hover": {
              backgroundColor: "#f5f5f5", // subtle hover
            },
          }}
        >
          <DeleteIcon />
        </IconButton>
      )}
    </Box>
  );

  return (
    <Box
      sx={{
        position: "relative",
        border: "1px solid rgba(0, 0, 0, 0.23)", // matches TextField default
        borderRadius: 1,
        padding: 2,
        marginTop: 4,
      }}
    >
      <Typography
        variant="caption"
        sx={{
          position: "absolute",
          top: 0,
          left: 8,
          transform: "translateY(-50%)",
          backgroundColor: "white",
          paddingX: 0.5,
          color: "rgba(0, 0, 0, 0.6)", // MUI label default
          fontSize: "0.75rem",
          lineHeight: 1,
        }}
      >
        Budget
      </Typography>

      {/* Existing Rows */}
      {/*
          row,
          index,
          nameDisabled,
          valueDisabled,
          deletable,
          showPercentage
        */}
      {unit_id === 4 &&
        rows?.[0]?.row_type === rowType.approvedBudget &&
        renderRow(rows[0], 0, true, false, false, false)}
      {unit_id === 4 &&
        rows?.[1]?.row_type === rowType.selfContribution &&
        renderRow(rows[1], 1, true, false, false, false)}
      {unit_id === 4 && rows?.[0]?.row_type === rowType.approvedBudget && (
        <Divider sx={{ my: 2, backgroundColor: "rgba(0, 0, 0, 0.23)" }} />
      )}
      {unit_id === 4 &&
        rows?.[2]?.row_type === rowType.total &&
        renderRow(rows[2], 2, true, true, false, false)}

      {unit_id !== 4 &&
        rows?.[2]?.row_type === rowType.total &&
        renderRow(rows[2], 2, true, false, false, false)}

      {renderRow(
        staffCost,
        staffCost.budget_row_id ?? -1,
        true,
        true,
        false,
        true,
      )}
      {rows &&
        rows.map((row, index) => {
          if (index < 3) return null;
          return renderRow(row, index, false, false, true, true);
        })}

      {/* Phantom Row */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          opacity: 0.5,
          width: "100%",
        }}
      >
        <TextField
          placeholder="Add Cost"
          size="small"
          fullWidth
          value={""}
          onChange={(e) => {
            if (createdRef.current) {
              commitPhantomRow(e.target.value, null, true);
              return;
            }
            createdRef.current = true;
            commitPhantomRow(e.target.value, null, false);
            suppressSelectRef.current = true;
            setTimeout(() => {
              rowNameRef.current?.focus();
            }, 0);
          }}
          inputRef={phantomNameRef}
        />
        <TextField
          placeholder="Value"
          type="decimal"
          size="small"
          fullWidth
          value={""}
          onChange={(e) => {
            if (createdRef.current) {
              commitPhantomRow(null, e.target.value, true);
              return;
            }
            createdRef.current = true;
            commitPhantomRow(null, e.target.value, false);
            suppressSelectRef.current = true;
            setTimeout(() => {
              rowValueRef.current?.focus();
            }, 0);
          }}
          InputProps={{
            startAdornment: <span style={{ marginLeft: 4 }}>€&nbsp;</span>,
          }}
        />
      </Box>
      {nameError && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {nameError}
        </Alert>
      )}

      <Divider sx={{ my: 2, backgroundColor: "rgba(0, 0, 0, 0.23)" }} />

      {/* EBS Row */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          marginTop: 2,
          marginLeft: 1,
          width: "100%",
        }}
      >
        <Typography color={ebs < 0 ? "red" : "inherit"} width="52%">
          EBS Revenue
        </Typography>
        <Typography color={ebs < 0 ? "red" : "inherit"} width="30%">
          €&nbsp;{formatCurrency(ebs) || ""}
        </Typography>

        <Box sx={{ display: "flex", alignItems: "center" }} width="18%">
          <Typography
            variant="caption"
            color={ebs < 0 ? "red" : "text.secondary"}
          >
            {getPercentage(ebs)}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default DynamicBudgetInput;
